<template lang="pug">

div.condition.hotness-filter

  v-select.filter__operator(
    :items="operators"
    item-text="name"
    v-model="selectedOperator"
    @input="changedOperator"
    return-object
    data-cy="hotness-condition-select"
  )
    template(slot="item" slot-scope="{ item }")
      div(:data-cy="getDropdownDataCy(item)") {{ item.name }}

  .filter__value(v-if="selectedOperator && selectedOperator.needValue")
    v-autocomplete.search(
      v-model="selectedChips"
      :items="allOptions"
      item-value="text"
      item-text="text"
      :search-input.sync="customChip"
      :menu-props="{ maxWidth: 'none'}"
      data-cy="hotness-chips"
      @keyup.enter="addChip"
      attach
      clearable
      deletable-chips
      open-on-clear
      return-object
      small-chips
      multiple
    )
      // Slot to show the hotness chips at selection chips.
      template(slot="selection" slot-scope="hotness")
        hotness(
          :selected="hotness.selected"
          @input="hotness.parent.selectItem(hotness.item)"
          @close="closeChip(hotness)"
          :key="JSON.stringify(hotness.item)"
          :name="hotness.item.text"
          :color="hotness.item.color"
          close
        )

      // Slot to override the select list item
      // to show a coloured hotness chip on each row
      template(slot="item" slot-scope="hotness")
        div.hotness-select-list-item
          v-list-tile-action
            v-icon.inactive(light) check_box_outline_blank
            v-icon.active(light) check_box
          v-list-tile-content
            v-list-tile-title
              hotness(
                :name="hotness.item.text"
                :color="hotness.item.color"
                :maxLengthName="26"
                :data-cy="getAutocompleteDataCy(hotness)"
              )

      template(
        slot="append-item"
        v-if="!chipAlreadyPresent && customChip.trim().length"
      )
        v-list-tile.click-to-add(@click="attemptAddCustomChip")
          v-list-tile-content
            v-list-tile-title
              em Click here to add '{{ customChip.trim() }}'

    v-tooltip(bottom)
      template(slot="activator")
        v-icon.help help
      span Type any custom hotness and press enter to add it to the list

</template>

<script>
import {
  ExactMatchQueryBuilder,
} from '@/Core/utilities/filter-query-builders/ExactMatchQueryBuilder';
import Hotness from '../../../Contacts/components/Hotness';

const UNKNOWN_CHIP_COLOR = '#bbbbbb';

export default {
  components: {
    Hotness,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    options: {
      type: Array,
      default: () => [],
    },
    operators: {
      type: Array,
      default: () => ExactMatchQueryBuilder.OperatorList,
    },
  },
  data() {
    return {
      /**
       * The operator from the list that's been selected
       * @type {Object}
       */
      selectedOperator: {},

      /**
       * The selected condition (operand and value)
       * @type {Object}
       */
      selectedChips: [],

      /**
       * The autocomplete plain string being searched
       */
      customChip: '',

      /**
       * Any and all custom chips that have been added
       */
      customChips: [],
    };
  },
  mounted() {

    this.selectedChips = this.value.value ? [...this.value.value] : [];
    this.addIncomingChips();
    this.setSelectedOperator();

    this.setDefaultSelectedValues();

    this.$watch('selectedOperator', (operator, lastOperator) => {
      this.emitSelected();
    });

    this.$watch('selectedChips', (selectedChips, lastSelectedValue) => {
      this.emitSelected();
      // If anything was added to the selected values, clear the custom chip
      if (selectedChips.length !== lastSelectedValue.length) {
        this.customChip = '';
      }
    });
  },

  computed: {

    /**
     * All options: custom chips + stock options
     */
    allOptions() {
      return this.customChips.concat(this.options);
    },

    /**
     * Check if the custom chip is already present in
     * all the options
     */
    chipAlreadyPresent() {
      if (!this.customChip) return true;
      return this.isPresentInList(this.allOptions, this.customChip);
    },

    /**
     * Get the selected hotness names
     */
    selectedChipNames() {
      return this.selectedChips.map(({ text }) => text)
    },
  },

  methods: {

    /**
     * The operator has been changed; update/clear the value as necessary
     * @param  {} operator
     * @return {}
     */
    changedOperator(operator) {
      if (operator.needValue === false) {
        this.selectedChips = [];
      }
    },

    /**
     * On mount, set the selected operator depending on
     * what's been passed in as selected
     */
    setSelectedOperator() {
      this.selectedOperator = this.operators.find(operator => {
        return operator.name === this.value.operator;
      });
    },

    /**
     * Check if any value is set
     * @return {Boolean}
     */
    setDefaultSelectedValues() {
      if (!this.selectedChips) this.selectedChips = [];
    },

    /**
     * Wrapper to emit condition operator & value
     */
    emitSelected() {
      const emitObject = {
        operator: this.selectedOperator.name,
        value: this.selectedChipNames,
      };

      this.$emit('input', emitObject);
    },

    /**
     * Check if the chip is present in the list, in the same or a different
     * letter casing.
     *
     * @param {array} list - the list of strings to check
     * @param {string} chip - the needle
     */
    isPresentInList(list, chip) {
      const match = list.find(option => {
        return option.text.trim().toLowerCase() === chip.trim().toLowerCase();
      });
      return !!match;
    },

    /**
     * Check if the chip is present in the list, at least in part.
     *
     * We are looking to see if the chip as entered so far is in the list
     * in part.
     *
     * Like "Ta" will match "Tag"; so we don't want to add "Ta" as its own
     * item when it already matches others.
     *
     * @param {array} list - the list of strings to check
     * @param {string} chip - the needle
     */
    numberPartiallyContainedInList(list, chip) {
      const matches = list.filter(option => {
        return option
          .text
          .trim()
          .toLowerCase()
          .includes(chip.toLowerCase());
      });

      return matches.length;
    },

    /**
     * On mount, some custom chips may be present in the selected chips.
     * They are stored in the database as strings and passed in as strings.
     *
     * Here, we compare the strings to the given options, and wrap them in
     * an object to give a color and assign the name.
     *
     * If no option is present for some chip, we add it as a custom chip
     *
     * @param {array} chips incoming to the component on mount
     */
    addIncomingChips() {

      if (!this.selectedChips) return;

      this.selectedChips.forEach((selectedChip, index) => {
        if (!selectedChip) return;
        const foundOption = this.options.find(option => {
          return selectedChip === option.text;
        });

        if (foundOption) {
          this.selectedChips.splice(index, 1, foundOption);
          return;
        }
        this.addCustomChip(selectedChip, index);
      });
    },

    /**
     * Add a custom chip to an index, if specified
     */
    addCustomChip(chip, index = null) {
      chip = chip.trim();
      const chipObject = { color: UNKNOWN_CHIP_COLOR, text: chip };
      this.customChips.push(chipObject);
      if (!this.selectedChipNames.includes(chip)) {
        if (index === null) {
          this.selectedChips.push(chipObject);
        } else {
          this.selectedChips.splice(index, 1, chipObject);
        }
      }
    },

    /**
     * Add the custom chip if it exists.
     *
     * This function is called on key.enter and on clicking the appended item
     * used to add custom chips.
     *
     * Return if the custom chip is empty, or already present in any of the option
     *
     * Otherwise, add this custom chip to the list, then clear it.
     */
    attemptAddCustomChip() {

      if (this.customChip.trim().length === 0) return;

      if (this.isPresentInList(this.allOptions, this.customChip)) return;

      this.addCustomChip(this.customChip);

      this.customChip = '';
    },

    /**
     * When enter is pressed, check if there's a chip and add it to the
     * custom list if it's not already there in some form.
     *
     * If this chip is not partially contained in the existing lists,
     * add it.
     *
     * If this chip is partially contained in one other, that item will
     * be selected in the drop-down and will be added automatically by
     * vuetify anyway. We simply clear the input.
     *
     * If there are 2+ matches, we do nothing. Don't clear, don't add new?
     *
     * One repercussion of this approach is that we can't add a tag
     * that is a substring of an already existing string.
     */
    addChip() {

      if (!this.customChip || !this.customChip.trim()) {
        return;
      }

      if (this.numberPartiallyContainedInList(this.allOptions, this.customChip) === 0) {
        this.attemptAddCustomChip();
      }

      if (this.numberPartiallyContainedInList(this.allOptions, this.customChip) === 1) {
        this.customChip = '';
      }
    },
    /**
     * Cypress test method for condition
    */
    getDropdownDataCy(item) {
      return `hotness-condition-${this.operators.indexOf(item)}`;
    },
    /**
     * Cypress test method for chips
    */
    getAutocompleteDataCy({ item }) {
      return `hotness-chip-${this.allOptions.indexOf(item)}`;
    },
    /**
     * Remove an element selected from the autocomplete control
    */
    closeChip(item) {
      this.selectedChips.splice(item.index, 1);
    },
  },
};
</script>
<style lang="sass" type="text/sass">

// Turn chip clicking off so the user can click anywhere in the type-ahead
// to open the dropdown.
.hotness-filter

  .filter__value
    display: flex
    width: 100%
    align-items: center
    .v-select.v-select--chips .v-select__selections
      min-height: 32px
      width: 32px
      flex-wrap: wrap
    .v-input__append-inner
      margin-top: 2px

  .v-list__tile

    &--active
      .hotness-select-list-item
        .v-list__tile__action
          .v-icon.active.theme--light
            display: inline-block
          .v-icon.inactive.theme--light
            display: none

    .hotness-select-list-item
      color: white
      display: flex

      .v-list__tile__action
        .v-icon.active
          display: none
        .v-icon.inactive
          display: inline-block

        .theme--light.v-icon
          color: var(--v-primary-base)
          width: 40px;
          padding-top: 5px;

      .v-list__tile__content
        padding-top: 13px;
        .hotness .v-chip__content
          cursor: pointer
</style>
